/**
 * «Перевести в расширенное» — у версии, сохранённой ОБЫЧНЫМ сообщением, когда политика бренда
 * «все посты — расширенным сообщением» включена (docs/release-2026-09-19.md §9.3).
 *
 * ЗАЧЕМ ОТДЕЛЬНОЕ ДЕЙСТВИЕ. Способ отправки выбирается при заморозке и дальше читается из
 * подписанного: версия, сохранённая до включения политики, уйдёт тем способом, который в неё
 * подписан, — обычным сообщением с карточкой. Задним числом её не перевести, иначе «что
 * подписали» и «что уйдёт» разошлись бы. Перевод — это НОВАЯ версия из тех же авторских частей:
 * тот же маршрут, что у правки человеком (`POST …/versions`), и сервер сам заморозит её по
 * политике бренда — rich, без карточки.
 *
 * Тело — текст и сущности СОХРАНЁННОЙ версии побайтово, без `link_preview_options` (карточка
 * следует за текстом, и у rich её снимет заморозка) и без `media` (наследуется). Ни одного
 * знака поста действие поменять не может: оно не принимает текста от человека вовсе.
 *
 * Где стоит: в плашке карточки под превью — там, где человек читает «снизу прицепится
 * карточка» и решает, нужна ли она. Объяснение — в ⓘ рядом (правило партии §1).
 */

import { type JSX, useState } from 'react';
import type { RenderModel } from '../../api/types.ts';
import { InfoPopover } from '../../components/InfoPopover.tsx';
import { isStaleBaseVersion, saveErrorText, saveVersion } from '../versionSave.ts';
import styles from './RichConvertAction.module.css';

export interface RichConvertActionProps {
  readonly model: RenderModel;
  /** Родилась новая версия (`version_no`) или экран устарел (`null`): перейти и перечитать. */
  readonly onConverted: (versionNo: number | null) => void;
}

export function RichConvertAction({ model, onConverted }: RichConvertActionProps): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const convert = (): void => {
    if (busy) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    saveVersion({
      draftId: model.draft_id,
      text: model.canonical.text,
      entities: model.canonical.entities,
      baseVersionNo: model.version_no,
    })
      .then((res) => {
        if (res.version_no > model.version_no) {
          onConverted(res.version_no);
          return;
        }
        // Сервер вернул ту же версию: содержание совпало, то есть заморозка выбрала тот же
        // способ отправки — политику успели выключить. Экран перечитывается, чтобы плашка
        // перестала предлагать перевод, которого не будет.
        setNotice(
          'Новой версии не появилось: сервер сохранил пост тем же способом — похоже, политику ' +
            '«Все посты — расширенным сообщением» только что выключили. Проверьте «Настройки».',
        );
        onConverted(null);
      })
      .catch((e: unknown) => {
        if (isStaleBaseVersion(e)) {
          setNotice(
            `Пока вы решали, появилась версия новее v${String(model.version_no)}: экран ` +
              'перезаполнен — переведите её, если перевод ещё нужен.',
          );
          onConverted(null);
          return;
        }
        setError(saveErrorText(e));
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <div className={styles['wrap']}>
      <div className={styles['row']}>
        <button
          type="button"
          className={busy ? 'btn btn-sm btn-dis' : 'btn btn-ghost btn-sm'}
          disabled={busy}
          onClick={convert}
        >
          {busy ? 'Переводим…' : 'Перевести в расширенное'}
        </button>
        <InfoPopover label="Зачем переводить в расширенное">
          <span>
            В «Настройках» включено «Все посты — расширенным сообщением», но эта версия сохранена
            обычным сообщением — до включения. Она уйдёт так, как подписана: обычным сообщением с
            карточкой превью ссылки.
          </span>
          <span>
            {`Перевод сохраняет тот же текст новой версией v${String(model.version_no + 1)}: она уйдёт расширенным сообщением, без карточки. Текст, разметка и картинка не меняются.`}
          </span>
          <span>
            Меняется подписываемое содержание, поэтому новую версию нужно утвердить заново.
          </span>
        </InfoPopover>
      </div>
      {error === null ? null : (
        <div className="val val-err" role="alert">
          <span>{error}</span>
        </div>
      )}
      {notice === null ? null : (
        <div className="val val-info">
          <span>{notice}</span>
        </div>
      )}
    </div>
  );
}
